'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import Modal from 'react-modal';
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHiveCartLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import { faXmark } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faLockKeyhole } from '@awesome.me/kit-94e593bf23/icons/classic/solid';

// Lazy load ExpressCheckout only when needed
const ExpressCheckout = dynamic(() => import('@/app/_components/ExpressCheckout'), {
  ssr: false
});
import { decodeEntities } from '@/app/_lib/services/ClientUtil';
import Price from '@/app/_components/Price';
import { useCart } from '@/app/_lib/hooks';

// Format unix timestamp to readable date
const formatBackorderDate = timestamp => {
  if (!timestamp) return null;
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};
const CartOptions = t0 => {
  const $ = _c(2);
  const {
    options
  } = t0;
  if (!options) {
    return null;
  }
  const now = Date.now() / 1000;
  let t1;
  if ($[0] !== options) {
    t1 = <>{Object.entries(options).map((option, index) => {
        const opt = option[1];
        const hasUpcharge = opt.option_price > 0 || opt.original_option_price > 0;
        const isDiscounted = opt.original_option_price > 0 && opt.original_option_price > opt.option_price;
        let optionBackorderMessage = null;
        if (opt.production_hold_expiration_date && opt.production_hold_expiration_date > now) {
          optionBackorderMessage = {
            prompt: "production hold",
            message: `estimated availability ${formatBackorderDate(opt.production_hold_expiration_date)}`
          };
        } else {
          if (opt.backorder_expiration_date && opt.backorder_expiration_date > now) {
            optionBackorderMessage = {
              prompt: "backordered",
              message: `estimated in stock ${formatBackorderDate(opt.backorder_expiration_date)}`
            };
          }
        }
        return <li key={index} className="text-2xs mb-1"><span className="text-ink-light capitalize">{opt.selection_label}:</span> <span className="font-medium">{opt.option_name}</span>{hasUpcharge && <span className="ml-1">{isDiscounted && <span className="line-through text-ink-light mr-1">+ <Price amount={opt.original_option_price} /></span>}<span className="font-medium text-black">+ <Price amount={opt.option_price} /></span></span>}{opt.option_photo && <div className="mt-1"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/option_photos/${opt.option_photo}`} alt={opt.option_name} width={30} height={20} /></div>}{optionBackorderMessage && <div className="mt-1"><span className="text-red">{optionBackorderMessage.prompt}</span> {optionBackorderMessage.message}</div>}</li>;
      })}</>;
    $[0] = options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const CartSwatches = t0 => {
  const $ = _c(2);
  const {
    swatches
  } = t0;
  if (!swatches) {
    return null;
  }
  const now = Date.now() / 1000;
  let t1;
  if ($[0] !== swatches) {
    t1 = <>{Object.entries(swatches).map((swatch, index) => {
        const sw = swatch[1];
        const swatchPrice = sw.swatch_price || sw.price || 0;
        const originalPrice = sw.original_swatch_price || 0;
        const hasUpcharge = swatchPrice > 0 || originalPrice > 0;
        const isDiscounted = originalPrice > 0 && originalPrice > swatchPrice;
        let swatchBackorderMessage = null;
        if (sw.production_hold_expiration_date && sw.production_hold_expiration_date > now) {
          swatchBackorderMessage = {
            prompt: "production hold",
            message: `estimated availability ${formatBackorderDate(sw.production_hold_expiration_date)}`
          };
        } else {
          if (sw.backorder_expiration_date && sw.backorder_expiration_date > now) {
            swatchBackorderMessage = {
              prompt: "backordered",
              message: `estimated in stock ${formatBackorderDate(sw.backorder_expiration_date)}`
            };
          }
        }
        return <li key={index} className="text-2xs mb-1"><span className="text-ink-light capitalize">{sw.swatch_set_label}:</span> <span className="font-medium capitalize">{sw.swatch_name}</span>{sw.swatch_code && <span className="text-ink-light ml-0.5">({sw.swatch_code})</span>}{hasUpcharge && <span className="ml-1">{isDiscounted && <span className="line-through text-ink-light mr-1">+ <Price amount={originalPrice} /></span>}<span className="font-medium text-black">+ <Price amount={swatchPrice} /></span></span>}{(sw.swatch_thumb || sw.swatch_photo) && <div className="mt-1"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/swatch_photos/${sw.swatch_thumb || sw.swatch_photo}`} alt={sw.swatch_name} width={30} height={30} /></div>}{swatchBackorderMessage && <div className="mt-1"><span className="text-red">{swatchBackorderMessage.prompt}</span> {swatchBackorderMessage.message}</div>}</li>;
      })}</>;
    $[0] = swatches;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const CartAddModal = t0 => {
  const $ = _c(42);
  const {
    isOpen,
    setIsOpen,
    modalContent: t1,
    onClose: t2
  } = t0;
  const modalContent = t1 === undefined ? false : t1;
  const onClose = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  useEffect(_temp, t3);
  const {
    cart
  } = useCart();
  let t4;
  if ($[1] !== onClose || $[2] !== setIsOpen) {
    t4 = () => {
      setIsOpen(false);
      if (onClose) {
        onClose();
      }
    };
    $[1] = onClose;
    $[2] = setIsOpen;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const closeModal = t4;
  const t5 = `fixed inset-0 top-[var(--header-height)] [body.header-collapsed_&]:top-[var(--header-height-collapsed)] lg:top-0 z-30 lg:z-[60] bg-menu-overlay transition-opacity duration-300 ${isOpen ? "visible opacity-100" : "invisible opacity-0 pointer-events-none"}`;
  const t6 = `fixed z-40 lg:z-[70] top-[var(--header-height)] [body.header-collapsed_&]:top-[var(--header-height-collapsed)] lg:top-0 w-full lg:w-1/3 lg:max-w-[450px] h-[calc(100dvh-var(--header-height))] [body.header-collapsed_&]:h-[calc(100dvh-var(--header-height-collapsed))] lg:h-dvh bg-white shadow-sm duration-500 transition-all flex flex-col outline-none ${isOpen ? "left-0 lg:left-auto lg:right-0" : "left-full lg:left-auto lg:-right-full"}`;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FontAwesomeIcon icon={faHiveCartLight} aria-hidden="true" className="text-xl mr-2 block" />;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== modalContent?.message) {
    t8 = <div className="flex items-center">{t7}{modalContent?.message ? <span className="text-md">{modalContent?.message}</span> : <span className="text-md">Shopping Cart</span>}</div>;
    $[5] = modalContent?.message;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <FontAwesomeIcon icon={faXmark} aria-hidden="true" className="text-lg block" />;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== closeModal) {
    t10 = <button type="button" onClick={closeModal} aria-label="Close cart" className="text-light hover:text-primary flex items-center">{t9}</button>;
    $[8] = closeModal;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t8) {
    t11 = <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-gray-8 leading-none">{t8}{t10}</div>;
    $[10] = t10;
    $[11] = t8;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== cart || $[14] !== closeModal) {
    t12 = cart?.cartItems?.length > 0 && <div className="mb-5">{cart.cartItems.map((cartItem, itemIndex) => {
        let imageUrl = "";
        if (cartItem.product?.variant?.file_name) {
          imageUrl = cartItem.product.variant.file_name;
        } else {
          if (cartItem.product?.option_product_photo) {
            imageUrl = `option_photos/${cartItem.product.option_product_photo}`;
          } else {
            if (cartItem.product?.cart_swatch_image) {
              imageUrl = `swatch_photos/${cartItem.product.cart_swatch_image}`;
            } else {
              if (cartItem.product?.thumb_1) {
                imageUrl = "thumbs/" + cartItem.product.thumb_1;
              } else {
                if (cartItem.product?.product_photo) {
                  imageUrl = cartItem.product.product_photo;
                }
              }
            }
          }
        }
        return <div key={itemIndex}>{imageUrl && <div className="flex items-start border-0 border-b-1 pb-3 mb-3"><Link href={`${cartItem.product?.url}`} onClick={closeModal} className="overlay-block w-[60px] mr-3 flex-shrink-0"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${imageUrl}`} alt={decodeEntities(cartItem.product?.sub_product_name)} width={60} height={60} quality={90} sizes="60px" className="object-contain" /></Link><div className="flex-1 min-w-0 mr-3 break-words"><h4 className="text-xs font-medium capitalize text-ink-dark mb-1">{decodeEntities(cartItem.product?.sub_product_name)}</h4><ul><CartOptions options={cartItem.product?.options} /><CartSwatches swatches={cartItem.product?.swatches} /></ul><div className="text-2xs mt-1"><span className="text-ink-light">Qty:</span><span className="font-medium ml-1">{cartItem.quantity}</span></div></div><div className="flex-shrink-0 text-right"><div className="text-sm font-medium"><Price amount={cartItem.total_price} /></div>{cartItem.original_total > cartItem.total_price && <div className="line-through text-ink-light text-2xs"><Price amount={cartItem.original_total} /></div>}{cartItem.product?.on_sale == 1 && cartItem.product?.discount_percentage > 0 && <div className="text-red text-2xs">{cartItem.product.discount_percentage}% off</div>}{cart?.isTradeCustomer && cartItem.trade_price !== undefined && <div className="text-2xs text-light">Trade Price <Price amount={cartItem.trade_price} /></div>}</div></div>}</div>;
      })}</div>;
    $[13] = cart;
    $[14] = closeModal;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12) {
    t13 = <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4">{t12}</div>;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <FontAwesomeIcon icon={faHiveCartLight} aria-hidden="true" className="absolute top-1/3 left-2 text-2xs" />;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = cart?.cartQuantity > 0 && `(${cart.cartQuantity} item${cart.cartQuantity > 1 ? "s" : ""})`;
  let t16;
  if ($[19] !== closeModal || $[20] !== t15) {
    t16 = <Link href="/cart" onClick={closeModal} className="btn btn-sm btn-default mb-2">{t14}View Cart {t15}</Link>;
    $[19] = closeModal;
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== cart?.cartItems?.length || $[23] !== closeModal) {
    t17 = cart?.cartItems?.length > 0 && <Link href="/checkout" onClick={closeModal} className="btn btn-sm btn-primary mb-2"><FontAwesomeIcon icon={faLockKeyhole} aria-hidden="true" className="absolute top-1/3 left-2 text-2xs" />Checkout</Link>;
    $[22] = cart?.cartItems?.length;
    $[23] = closeModal;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== cart) {
    t18 = cart && cart.subTotal > 0 && <ExpressCheckout expressCheckoutData={cart.expressCheckoutData} displayExpressPaymentIcons={true} displayStandardPaymentIcons={true} usePanel={false} className="mb-2 w-full" />;
    $[25] = cart;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== closeModal) {
    t19 = <button onClick={closeModal} className="text-2xs text-light">Continue Shopping</button>;
    $[27] = closeModal;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t16 || $[30] !== t17 || $[31] !== t18 || $[32] !== t19) {
    t20 = <div className="shrink-0 px-6 py-4 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] flex flex-col items-center">{t16}{t17}{t18}{t19}</div>;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== closeModal || $[35] !== isOpen || $[36] !== t11 || $[37] !== t13 || $[38] !== t20 || $[39] !== t5 || $[40] !== t6) {
    t21 = <Modal isOpen={isOpen} onRequestClose={closeModal} contentLabel="Shopping Cart" overlayClassName={t5} className={t6} closeTimeoutMS={500} bodyOpenClassName="ReactModal__Body--open" htmlOpenClassName="ReactModal__Html--open">{t11}{t13}{t20}</Modal>;
    $[34] = closeModal;
    $[35] = isOpen;
    $[36] = t11;
    $[37] = t13;
    $[38] = t20;
    $[39] = t5;
    $[40] = t6;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  return t21;
};
export default CartAddModal;
function _temp() {
  Modal.setAppElement("#app");
}